import React, { useEffect, useMemo, useState } from 'react';
import styles from './LogsPage.module.css';
import { getSystemLogs, deleteLogsForPeriod } from '../api/systemLogsApi';
import AppLayout from '../components/AppLayout';
import { formatDateTimeDDMMYYYY } from '../utils/dateFormat';

const initialFilters = {
  action: '',
  username: '',
  startDate: '',
  endDate: '',
};

const LogsPage = () => {
  const [filters, setFilters] = useState(initialFilters);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedLogId, setExpandedLogId] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  const token = useMemo(() => localStorage.getItem('authToken'), []);
  const userInfo = useMemo(() => {
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return {
          userType: payload.userType,
          requesterUsername: payload.username,
        };
      } catch (error) {
        console.error('Error parsing token payload:', error);
        return { userType: null, requesterUsername: null };
      }
    }
    return { userType: null, requesterUsername: null };
  }, [token]);

  const fetchLogs = async (currentFilters = filters) => {
    setLoading(true);
    setErrorMessage('');
    try {
      const response = await getSystemLogs(currentFilters);
      setLogs(response.data);
    } catch (error) {
      console.error('Failed to fetch logs:', error);
      setErrorMessage('Unable to retrieve system logs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApply = () => {
    fetchLogs(filters);
  };

  const handleReset = () => {
    setFilters(initialFilters);
    fetchLogs(initialFilters);
  };

  const handleDeleteLogs = async () => {
    if (!filters.startDate || !filters.endDate) {
      window.alert('Please choose a start and end date before deleting logs.');
      return;
    }
    if (!window.confirm('Are you sure you want to delete logs for the selected period?')) {
      return;
    }

    setLoading(true);
    try {
      const result = await deleteLogsForPeriod(filters.startDate, filters.endDate);
      window.alert(`Deleted ${result.deletedCount || 0} logs.`);
      await fetchLogs(filters);
    } catch (error) {
      console.error('Failed to delete logs:', error);
      window.alert('Failed to delete logs. Please check the console for details.');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => window.print();

  const toggleExpand = (id) => {
    setExpandedLogId((prev) => (prev === id ? null : id));
  };

  const formatTimestamp = (value) => {
    return formatDateTimeDDMMYYYY(value, value);
  };

  return (
    <AppLayout
      title="System Logs"
      userType={userInfo.userType}
      showHome
      homeLabel="Homepage"
      contentClassName={styles.page}
      contentProps={{ 'aria-busy': loading }}
    >
      {loading && (
        <div className={styles.loadingOverlay}>
          <div className={styles.spinner}></div>
        </div>
      )}

      <section className={styles.toolbar}>
        <div className={styles.toolInputs}>
          <input
            type="text"
            placeholder="Action…"
            value={filters.action}
            onChange={(event) =>
              setFilters((prev) => ({ ...prev, action: event.target.value }))
            }
            className={styles.toolInput}
          />
          <input
            type="text"
            placeholder="Username…"
            value={filters.username}
            onChange={(event) =>
              setFilters((prev) => ({ ...prev, username: event.target.value }))
            }
            className={styles.toolInput}
          />
          <input
            type="date"
            value={filters.startDate}
            onChange={(event) =>
              setFilters((prev) => ({ ...prev, startDate: event.target.value }))
            }
            className={styles.toolInput}
          />
          <input
            type="date"
            value={filters.endDate}
            onChange={(event) =>
              setFilters((prev) => ({ ...prev, endDate: event.target.value }))
            }
            className={styles.toolInput}
          />
        </div>
        <div className={styles.toolActions}>
          <button type="button" className={styles.primaryButton} onClick={handleApply}>
            Apply
          </button>
          <button type="button" className={styles.secondaryButton} onClick={handleReset}>
            Reset
          </button>
          <button type="button" className={styles.ghostButton} onClick={handlePrint}>
            Print
          </button>
          <button type="button" className={styles.dangerButton} onClick={handleDeleteLogs}>
            Delete range
          </button>
        </div>
      </section>

      {errorMessage && (
        <div className={styles.errorBanner} role="alert">
          {errorMessage}
        </div>
      )}

      {logs.length === 0 && !loading ? (
        <div className={styles.emptyState}>
          <h3>No logs found</h3>
          <p>Adjust your filters or try a different date range.</p>
        </div>
      ) : (
        <section className={styles.logList}>
          {logs.map((log) => {
            const isExpanded = expandedLogId === log._id;
            return (
              <article key={log._id} className={styles.logCard}>
                <div className={styles.cardHeader}>
                  <div className={styles.cardMeta}>
                    <span className={styles.metaUser}>{log.username}</span>
                    <span className={styles.metaAction}>{log.action}</span>
                  </div>
                  <time className={styles.metaTime}>{formatTimestamp(log.createdAt)}</time>
                </div>

                {isExpanded ? (
                  <pre className={styles.details} aria-live="polite">
                    {JSON.stringify(log.details, null, 2)}
                  </pre>
                ) : (
                  <p className={styles.summary} aria-label="Log summary">
                    {log.details?.description || 'Details available'}
                  </p>
                )}

                <div className={styles.cardActions}>
                  <button
                    type="button"
                    className={styles.linkButton}
                    onClick={() => toggleExpand(log._id)}
                  >
                    {isExpanded ? 'Hide details' : 'Show details'}
                  </button>
                </div>
              </article>
            );
          })}
        </section>
      )}
    </AppLayout>
  );
};

export default LogsPage;
